import { Injectable, inject } from '@angular/core';
import { ApiProvider } from './helpers/api-provider';
import { HttpHeaders } from '@angular/common/http';
import { LanguageService } from '../language.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiProviderFactoryService } from './helpers/api-provider-factory.service';
import { environment } from '../../../../environments/environment';
import { isDesktop, isTablet } from '../../signals/device';

@Injectable({
  providedIn: 'root',
})
export class CmsApiService {
  private _lang = inject(LanguageService);
  private _apiFactory = inject(ApiProviderFactoryService);


  /**
   * For API communication with lang segment
   */
  private _api: ApiProvider = this._apiFactory.create({
    defaultOptions: {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'DEVICE-TYPE': isDesktop() ? 'Desktop' : isTablet() ? 'Tablet' : 'Mobile',
      }),
      withCredentials: true,
    },
    host: environment.api_host,
    urlResolutionFn: () => this._lang.langChange$.pipe(
      map((lang: string) => {
        switch (lang) {
          case 'en-nz':
            return '/nz';
          case 'en-au':
            return '/au';
          case 'en-ca':
            return '/ce';
          default:
            return `/${lang}`;
        }
      }),
    ),
  });

  /**
   * For API communication without lang segment
   */
  private _noLangApi: ApiProvider = this._apiFactory.create({
    defaultOptions: {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'DEVICE-TYPE': isDesktop() ? 'Desktop' : isTablet() ? 'Tablet' : 'Mobile',

      }),
      withCredentials: true,
    },
    host: environment.api_host,
  });

  /**
   * For API communication without lang segment
   */
  private _errorApi: ApiProvider = this._apiFactory.create({
    defaultOptions: {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'DEVICE-TYPE': isDesktop() ? 'Desktop' : isTablet() ? 'Tablet' : 'Mobile',

      }),
      withCredentials: true,
    },
    host: environment.error_host + '/api',
  });

  private _hostApi: ApiProvider = this._apiFactory.create({
    defaultOptions: {
      headers: new HttpHeaders({
        'Content-Type': 'text/html; charset=UTF-8',
        Accept: 'text/html',
      }),
      responseType: 'text',
    },
    host: environment.cms_host,
  });

  public get customHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
    });
  }

  public get customHeadersList(): any[] {
    return this._api.customHeadersList;
  }

  public get customNoLangHeadersList(): any[] {
    return this._noLangApi.customHeadersList;
  }

  /**
   * /banner/list
   *
   * @param params
   */
  bannerList(params: object = {}): Observable<any> {
    return this._api.getCached('/banner/list', params);
  }

  /**
   * /banner/item
   *
   * @param params
   */
  bannerItem(params: object = {}): Observable<any> {
    return this._api.getCached('/banner/item', params);
  }

  /**
   * /page/item
   *
   * @param params
   */
  pageItem(params: object = {}): Observable<any> {
    return this._api.getCached('/page/item', params);
  }

  /**
   * /payment/list
   *
   * @param params
   */
  paymentList(params: object = {}): Observable<any> {
    return this._api.getCached('/payment/list', params);
  }

  /**
   * /promotion/item
   *
   * @param params
   */
  promotionItem(params: object = {}): Observable<any> {
    return this._api.getCached('/promotion/item', params);
  }

  /**
   * /promotion/list
   *
   * @param params
   */
  promotionList(params: object = {}): Observable<any> {
    return this._api.getCached('/promotion/list', params);
  }

  /**
   * /game/provider/list
   *
   * @param params
   */
  gameProviderList(params: object = {}): Observable<any> {
    return this._api.getCached('/game/provider/list', params);
  }

  /**
   * /tournament/item
   *
   * @param params
   */
  tournamentItem(params: object = {}): Observable<any> {
    return this._api.getCached('/tournament/item', params);
  }

  /**
   * /tournament/list
   *
   * @param params
   */
  tournamentList(params: object = {}): Observable<any> {
    return this._api.getCached('/tournament/list', params);
  }
  /**
   * /static-content/item
   *
   * @param params
   */
  staticContentItem(params: object = {}): Observable<any> {
    return this._api.getCached('/static-content/item', params);
  }

  /**
   * /static-content/list
   *
   * @param params
   */
  staticContentList(params: object = {}): Observable<any> {
    return this._api.getCached('/static-content/list', params);
  }

  /**
   * /game/list
   *
   * @param params
   */
  gameList(params: object = {}): Observable<any> {
    return this._api.getCached('/game/list', params);
  }

  /**
   * /game/content
   *
   * @param params
   */
  gameContent(params: object = {}): Observable<any> {
    return this._api.getCached('/game/content/item', params);
  }

  /**
   * /game/list
   *
   * @param params
   */
  gameListNoCache(params: object = {}): Observable<any> {
    return this._api.get('/game/list', params);
  }

  /**
   * /currency/list/ss
   *
   * @param params
   */
  currencyListSS(params: object = {}): Observable<any> {
    return this._api.getCached('/currency/list/ss', params);
  }

  /**
   * /country/list
   *
   * @param params
   */
  countryList(params: object = {}): Observable<any> {
    return this._api.getCached('/country/list', params);
  }

  /**
   * /service/environment
   *
   * @param params
   */
  serviceEnvironment(params: object = {}): Observable<any> {
    return this._noLangApi.get('/service/environment');
  }
  /**
   * /localization/list
   *
   * @param params
   */
  localizationList(params: object = {}): Observable<any> {
    return this._api.getCached('/localization/list', params);
  }

  /**
   * /sys-notification/item
   *
   * @param params
   */
  sysNotificationItem(params: object = {}): Observable<any> {
    return this._api.getCached('/sys-notification/item', params);
  }

  /**
   * /news/list
   *
   * @param params
   */
  newsList(params: object = {}): Observable<any> {
    return this._api.getCached('/news/list', params);
  }

  /**
   * /news/item
   *
   * @param params
   */
  newsItem(params: object = {}): Observable<any> {
    return this._api.getCached('/news/item', params);
  }

  /**
   * /game/category/list
   * @param params
   */
  gameCategoryAttrList(params: object = {}): Observable<any> {
    return this._api.getCached('/game/category/attribute/list', params);
  }

  /**
   * POST /account/favorite
   *
   * @param data
   * @param params
   */
  postAccountFavorite(data: object = {}, params: object = {}) {
    return this._api.post('/account/favorite', data, { params });
  }

  /**
   * DELETE /account/favorite
   *
   * @param data
   * @param params
   */
  deleteAccountFavorite(data: object = {}, params: object = {}) {
    return this._api.delete('/account/favorite', data, { params });
  }

  /**
   * /exchange/rate/crypto?from=${currency}&to=${currency}
   *
   * @param data - {from: string, to: string}
   * @param params
   */
  getExchangeRate(params: object = {}, options: object = {}): Observable<any> {
    return this._api.get('/exchange/rate/crypto', params, options);
  }

  /**
   * /currency/default
   * @param params
   * @param options
   */
  getAbTest(params: object = {}, options: object = {}): Observable<any> {
    return this._noLangApi.get('/service/abtest', params, options);
  }

  /**
   * GET /player/statistic/bet
   *
   */
  playerBetStatistic(params: object): Observable<any> {
    return this._noLangApi.getCached('/player/statistic/bet/new', params);
  }

  /**
   * GET /service/cache/cf
   * @param params
   * @param options
   */
  getCacheCf(params: object = {}, options: object = {}): Observable<any> {
    return this._noLangApi.get('/service/cache/cf', params, options);
  }

  /**
   * POST /bonus/check
   * @param data
   * @param params
   */
  postBonusCheck(data: string, params: object = {}): Observable<any> {
    return this._noLangApi.post('/bonus/check', data, { params });
  }

  /**
   * GET /leaderboard/list
   *
   * @param params
   */
  getLeaderBoard(params: object = {}): Observable<any> {
    return this._api.getCached('/leaderboard/list', params);
  }

  numberOfDepositDaysPerMonth(userId: number): Observable<any> {
    return this._noLangApi.get(`/player/statistic/deposit?player_id=${userId}`);
  }

  depositCountPerMonth(userId: number, currency: string) {
    return this._noLangApi.get(`/player/statistic/deposit/count_median?player_id=${userId}&currency=${currency}`);
  }

  medianDepositPerTransaction(userId: number, currency: string) {
    return this._noLangApi.get(`/player/statistic/deposit/count_median?player_id=${userId}&currency=${currency}`);
  }

  wageringPerMonth(userId: number, currency: string) {
    return this._noLangApi.get(`/player/statistic/bet/wagering?player_id=${userId}&currency=${currency}`);
  }

  differentGameProviders(userId: number): Observable<any> {
    return this._noLangApi.get(`/player/statistic/provider/count?player_id=${userId}`);
  }

  consecutiveMonthOfActivity(userId: number) {
    return this._noLangApi.get(`/player/statistic/bet/consecutive_month_activity?player_id=${userId}`);
  }

  /**
   * POST /user/deposit/ubidex/log
   * @param params
   */
  postUbidexLog(data: object = {}, params: object = {}) {
    return this._noLangApi.post('/user/deposit/ubidex/log', data, {params});
  }

}
